import {
  sanitizeCustomerBreakdownItems,
  sanitizeCustomerQuote,
} from './customerPricingVisibility';

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const roundMoney = (value) => Math.round(toNumber(value, 0) * 100) / 100;

const readLabel = (entry = {}) =>
  String(
    entry.label ||
      entry.display_name ||
      entry.displayName ||
      entry.description ||
      entry.name ||
      entry.rule_name ||
      ''
  ).trim();

const readAmount = (entry = {}) => roundMoney(entry?.amount ?? entry?.value ?? 0);
const findBreakdownAmount = (items = [], pattern) => {
  const match = (Array.isArray(items) ? items : []).find((item) => pattern.test(readLabel(item)));
  return match ? readAmount(match) : 0;
};

const hasBreakdownMatch = (items = [], pattern) =>
  (Array.isArray(items) ? items : []).some((item) => pattern.test(readLabel(item)));

const normalizeLines = (items = [], fallbackType = 'fee') =>
  (Array.isArray(items) ? items : [])
    .map((item, index) => {
      const amount = roundMoney(item?.amount ?? item?.value ?? 0);
      if (!amount) return null;
      return {
        id: item?.id ?? `${fallbackType}-${index + 1}`,
        label: readLabel(item) || (fallbackType === 'discount' ? 'Discount' : 'Mandatory fee'),
        amount,
        type: item?.type || fallbackType,
      };
    })
    .filter(Boolean);

const resolveDiscountLines = (source = {}) => {
  const discounts = normalizeLines(source?.discounts, 'discount').map((item) => ({
    ...item,
    amount: Math.abs(item.amount),
  }));
  if (discounts.length) return discounts;

  const appliedDiscountLabel =
    source?.applied_promo_code?.code ||
    source?.applied_promotion?.title ||
    source?.appliedPromotion?.title ||
    '';
  const discountAmount = toNumber(
    source?.discount_amount ?? source?.discountAmount ?? source?.total_discount ?? source?.discountTotal
  );
  if (!discountAmount) return [];
  return [
    {
      id: 'discount',
      label: appliedDiscountLabel || 'Discount',
      amount: Math.abs(roundMoney(discountAmount)),
      type: 'discount',
    },
  ];
};

const resolveMandatoryFeeLines = (source = {}) => {
  const breakdown =
    source?.tax_fee_breakdown ||
    source?.taxFeeBreakdown ||
    source?.taxes_and_fees ||
    source?.taxesAndFees ||
    null;
  const feeLines = normalizeLines(
    breakdown?.fee_lines || breakdown?.fees || source?.fees_breakdown || [],
    'fee'
  );
  if (feeLines.length) return feeLines;

  const mandatoryFees = toNumber(
    source?.mandatory_fees ?? source?.mandatoryFees ?? source?.fees_total ?? source?.fee_total ?? source?.fees
  );
  if (!mandatoryFees) return [];
  return [{ id: 'mandatory-fees', label: 'Mandatory fees', amount: roundMoney(mandatoryFees), type: 'fee' }];
};

export const buildNormalizedPricingBreakdownItems = ({
  basePrice = 0,
  discountLines = [],
  taxableSubtotal = 0,
  mandatoryFeeLines = [],
  gstAmount = 0,
  finalPayable = 0,
}) => {
  const items = [];
  const normalizedBasePrice = roundMoney(basePrice);
  const normalizedTaxableSubtotal = roundMoney(taxableSubtotal);
  const normalizedGstAmount = roundMoney(gstAmount);
  const normalizedFinalPayable = roundMoney(finalPayable);

  if (normalizedBasePrice) {
    items.push({ label: 'Base Price', amount: normalizedBasePrice, type: 'base' });
  }

  normalizeLines(discountLines, 'discount').forEach((line) => {
    items.push({
      id: line.id,
      label: line.label,
      amount: -Math.abs(line.amount),
      type: line.type,
    });
  });

  if (normalizedBasePrice || items.length) {
    items.push({ label: 'Taxable Subtotal', amount: normalizedTaxableSubtotal, type: 'taxable_subtotal' });
  }

  normalizeLines(mandatoryFeeLines, 'fee').forEach((line) => {
    items.push({
      id: line.id,
      label: line.label,
      amount: line.amount,
      type: 'fee',
    });
  });

  if (normalizedGstAmount) {
    items.push({ label: 'GST', amount: normalizedGstAmount, type: 'tax' });
  }

  items.push({ label: 'Final payable amount', amount: normalizedFinalPayable, type: 'total' });
  return items;
};

export const normalizeBookingPricing = (source = {}, options = {}) => {
  const visible = sanitizeCustomerQuote(source || {});
  const nights = Math.max(0, toNumber(visible?.nights ?? options.nights, 0));
  const currency = visible?.currency || options.currency || 'INR';
  const rawProvidedBreakdown = source?.line_item_breakdown || source?.breakdownItems || [];
  const providedBreakdown = sanitizeCustomerBreakdownItems(
    visible?.line_item_breakdown || visible?.breakdownItems || []
  );
  const breakdownBasePrice = findBreakdownAmount(providedBreakdown, /^base price$/i);
  const breakdownTaxableSubtotal = findBreakdownAmount(providedBreakdown, /taxable subtotal/i);
  const breakdownGstAmount = findBreakdownAmount(providedBreakdown, /\bgst\b/i);
  const breakdownFinalPayable = findBreakdownAmount(providedBreakdown, /final payable amount|total amount/i);
  const supplierBaseAmount = roundMoney(
    source?.supplier_base_amount ??
      source?.provider_quote?.netAmountBeforeTax ??
      findBreakdownAmount(rawProvidedBreakdown, /supplier base/i)
  );
  const supplierEmbeddedTaxAmount = roundMoney(
    source?.supplier_embedded_tax_amount ??
      source?.provider_quote?.gstAmount ??
      findBreakdownAmount(rawProvidedBreakdown, /supplier gst/i)
  );
  const inferredGstRate = roundMoney(
    visible?.gst_rate ??
      source?.provider_quote?.nightPrices?.[0]?.gstPercentage ??
      source?.provider_quote?.gstPercentage ??
      (supplierBaseAmount > 0 ? (supplierEmbeddedTaxAmount / supplierBaseAmount) * 100 : 0)
  );
  let basePrice = roundMoney(
    visible?.base_price ??
      visible?.basePrice ??
      visible?.base_nights_amount ??
      visible?.roomSubtotal ??
      visible?.base ??
      breakdownBasePrice ??
      0
  );
  const inferredDiscountAmount = roundMoney(resolveDiscountLines(visible).reduce((sum, item) => sum + Math.abs(item.amount || 0), 0));
  let discountAmount = roundMoney(
    visible?.discount_amount ??
      visible?.discountAmount ??
      visible?.total_discount ??
      visible?.discountTotal ??
      visible?.discount ??
      inferredDiscountAmount
  );
  let taxableSubtotal = roundMoney(
    visible?.taxable_subtotal ??
      visible?.taxableSubtotal ??
      breakdownTaxableSubtotal ??
      Math.max(basePrice - discountAmount, 0)
  );
  let mandatoryFees = roundMoney(
    visible?.mandatory_fees ??
      visible?.mandatoryFees ??
      visible?.fees_total ??
      visible?.fee_total ??
      visible?.fees ??
      0
  );
  let gstAmount = roundMoney(
    visible?.gst_amount ??
      visible?.gstAmount ??
      visible?.taxes_total ??
      visible?.tax_total ??
      visible?.tax ??
      breakdownGstAmount ??
      0
  );
  let explicitFinalPayable = roundMoney(
    visible?.final_payable ??
      visible?.finalPayable ??
      visible?.total_payable ??
      visible?.final_total ??
      visible?.grand_total ??
      visible?.grandTotal ??
      visible?.total ??
      breakdownFinalPayable ??
      0
  );
  const usesInclusiveSupplierSnapshot =
    String(source?.tax_display_mode || visible?.tax_display_mode || '').trim().toLowerCase() === 'inclusive' &&
    supplierEmbeddedTaxAmount > 0 &&
    breakdownBasePrice > 0;
  if (usesInclusiveSupplierSnapshot) {
    basePrice = breakdownBasePrice;
    discountAmount = roundMoney(Math.max(discountAmount, inferredDiscountAmount));
    taxableSubtotal = roundMoney(Math.max(basePrice - discountAmount, 0));
    gstAmount = roundMoney(
      gstAmount > 0
        ? gstAmount
        : taxableSubtotal * (Math.max(0, inferredGstRate) / 100)
    );
    explicitFinalPayable = roundMoney(
      breakdownFinalPayable ||
      taxableSubtotal + Math.max(mandatoryFees, 0) + Math.max(gstAmount, 0)
    );
  }
  const canonicalFinalPayable = roundMoney(
    Math.max(taxableSubtotal, 0) + Math.max(mandatoryFees, 0) + Math.max(gstAmount, 0)
  );
  const finalPayable =
    basePrice || discountAmount || taxableSubtotal || mandatoryFees || gstAmount
      ? canonicalFinalPayable
      : explicitFinalPayable;
  const discountLines = resolveDiscountLines(visible);
  const mandatoryFeeLines = resolveMandatoryFeeLines(visible);
  const hasTaxableSubtotalRow = hasBreakdownMatch(providedBreakdown, /taxable subtotal/i);
  const hasFinalPayableRow = hasBreakdownMatch(providedBreakdown, /final payable amount|total amount/i);
  const hasGstRow = hasBreakdownMatch(providedBreakdown, /\bgst\b/i);
  const providedTotalRow =
    (Array.isArray(providedBreakdown) ? providedBreakdown : []).find((item) =>
      /final payable amount|total amount/i.test(readLabel(item))
    ) || null;
  const providedTotalMatchesFormula =
    !providedTotalRow || Math.abs(readAmount(providedTotalRow) - finalPayable) <= 0.01;
  const canUseProvidedBreakdown =
    providedBreakdown.length &&
    hasTaxableSubtotalRow &&
    hasFinalPayableRow &&
    (gstAmount > 0 ? hasGstRow : true) &&
    providedTotalMatchesFormula;
  const breakdownItems =
    canUseProvidedBreakdown
      ? providedBreakdown
      : buildNormalizedPricingBreakdownItems({
          basePrice,
          discountLines,
          taxableSubtotal,
          mandatoryFeeLines,
          gstAmount,
          finalPayable,
        });

  const forceExclusiveTaxDisplay =
    usesInclusiveSupplierSnapshot ||
    basePrice > 0 ||
    taxableSubtotal > 0 ||
    gstAmount > 0 ||
    hasTaxableSubtotalRow ||
    hasGstRow;

  return {
    basePrice,
    base: basePrice,
    discountAmount,
    discount: discountAmount,
    taxableSubtotal,
    mandatoryFees,
    fees: mandatoryFees,
    gstAmount,
    tax: gstAmount,
    finalPayable,
    total: finalPayable,
    nights,
    currency,
    nightlyRate: nights > 0 ? roundMoney(taxableSubtotal / nights) : 0,
    estimatedStayTotal: taxableSubtotal,
    taxDisplayMode: forceExclusiveTaxDisplay ? 'exclusive' : visible?.tax_display_mode || 'exclusive',
    breakdownItems,
    discountLines,
    mandatoryFeeLines,
    taxFeeBreakdown:
      visible?.tax_fee_breakdown ||
      visible?.taxFeeBreakdown ||
      visible?.taxes_and_fees ||
      visible?.taxesAndFees ||
      null,
    paymentOptions: visible?.payment_options || visible?.paymentOptions || null,
  };
};
